import { engineBaseURL, engineClient } from './client';
import extractErrorMessage from '../utils/extractErrorMessage';

// Rules for messages the AI assistant starts on its own ("AI pravila"). They live in the AI
// engine, which records every change with the signed-in user. Every call returns
// { data, success, status, message } and never throws.

const notConfigured = {
  data: null,
  success: false,
  status: null,
  message: 'AI engine nije dostupan (nedostaje adresa API-ja).',
};

// An AI draft or the first "who would get it" count can take 15-30 s.
const SLOW = { timeout: 90000 };

const call = async (fn, fallback) => {
  if (!engineBaseURL) return notConfigured;
  try {
    const body = (await fn()).data;
    if (!body || typeof body.success !== 'boolean') {
      return {
        ...notConfigured,
        message: 'AI engine je vratio neočekivan odgovor.',
      };
    }
    return { ...body, status: 200 };
  } catch (error) {
    const status = error.response?.status ?? null;
    return {
      data: null,
      success: false,
      status,
      message:
        status === 403
          ? 'Nemate dozvolu za ovu radnju (AI pravila).'
          : extractErrorMessage(error, fallback),
    };
  }
};

const outreachRulesApi = {
  list: (includeDeleted = false) =>
    call(
      () =>
        engineClient.get('/outreach-rules', {
          params: includeDeleted ? { deleted: 'true' } : undefined,
        }),
      'Pravila nisu dostupna.'
    ),
  history: (ruleId) =>
    call(
      () => engineClient.get(`/outreach-rules/${ruleId}/history`),
      'Istorija nije dostupna.'
    ),
  allHistory: () =>
    call(
      () =>
        engineClient.get('/outreach-rules/history', { params: { limit: 200 } }),
      'Istorija nije dostupna.'
    ),
  generate: (prompt, ruleId) =>
    call(
      () =>
        engineClient.post(
          '/outreach-rules/generate',
          { prompt, ...(ruleId ? { ruleId } : {}) },
          SLOW
        ),
      'AI nije uspeo da napravi pravilo.'
    ),
  preview: (rule) =>
    call(
      () => engineClient.post('/outreach-rules/preview', { rule }, SLOW),
      'Provera pravila nije uspela.'
    ),
  review: (rule) =>
    call(
      () => engineClient.post('/outreach-rules/review', { rule }, SLOW),
      'Asistent nije uspeo da proveri pravilo.'
    ),
  create: (payload) =>
    call(
      () => engineClient.post('/outreach-rules', payload),
      'Pravilo nije sačuvano.'
    ),
  update: (ruleId, payload) =>
    call(
      () => engineClient.put(`/outreach-rules/${ruleId}`, payload),
      'Pravilo nije sačuvano.'
    ),
  setStatus: (ruleId, status) =>
    call(
      () => engineClient.patch(`/outreach-rules/${ruleId}/status`, { status }),
      'Status nije promenjen.'
    ),
  remove: (ruleId) =>
    call(
      () => engineClient.delete(`/outreach-rules/${ruleId}`),
      'Pravilo nije obrisano.'
    ),
  restore: (ruleId) =>
    call(
      () => engineClient.post(`/outreach-rules/${ruleId}/restore`, {}),
      'Pravilo nije vraćeno.'
    ),
  settings: () =>
    call(
      () => engineClient.get('/outreach-rules/settings'),
      'Podešavanja nisu dostupna.'
    ),
  saveSettings: (settings, note) =>
    call(
      () =>
        engineClient.put('/outreach-rules/settings', {
          settings,
          ...(note ? { note } : {}),
        }),
      'Podešavanja nisu sačuvana.'
    ),
};

export default outreachRulesApi;
